import { useEffect, useState, useMemo } from 'react'
import { useReadContract, useReadContracts, useAccount } from 'wagmi'
import { BaseVibeAbi } from '../utils/BaseVibeAbi'
import { CONTRACT_ADDRESS } from '../utils/constants'

interface LeaderboardEntry {
  address: string
  streak: number
  rank: number
  lastVote: number
  longestStreak: number
  username?: string
  avatar?: string
}

const ITEMS_PER_PAGE = 15
const MAX_LEADERBOARD_SIZE = 100
const NEYNAR_API_KEY = import.meta.env.VITE_NEYNAR_API_KEY

export default function Leaderboard() {
  const { address: userAddress } = useAccount()
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [userEntry, setUserEntry] = useState<LeaderboardEntry | null>(null)
  const [page, setPage] = useState(1)
  const [profileMap, setProfileMap] = useState<Record<string, { username: string, pfp_url: string }>>({})
  const [rawEntries, setRawEntries] = useState<LeaderboardEntry[]>([])

  // 1. Get total number of users
  const { data: totalUsers } = useReadContract({
    address: CONTRACT_ADDRESS as `0x${string}`,
    abi: BaseVibeAbi,
    functionName: 'getTotalUsers',
    query: {
      refetchInterval: 60000,
    },
  })

  // 2. Prepare calls to fetch all voter addresses
  const voterCalls = useMemo(() => {
    const total = totalUsers ? Number(totalUsers) : 0
    const calls = []
    for (let i = 0; i < total; i++) {
      calls.push({
        address: CONTRACT_ADDRESS as `0x${string}`,
        abi: BaseVibeAbi,
        functionName: 'voters',
        args: [BigInt(i)],
      })
    }
    return calls
  }, [totalUsers])

  // 3. Execute multicall to get all addresses
  const { data: voterAddresses } = useReadContracts({
    contracts: voterCalls,
    query: {
      enabled: voterCalls.length > 0,
      staleTime: 60000,
    },
  })

  // 4. Prepare calls to fetch streak data for each address
  const streakCalls = useMemo(() => {
    if (!voterAddresses) return []
    return voterAddresses
      .map((response) => response.result as unknown as string)
      .filter((addr) => !!addr)
      .map((addr) => ({
        address: CONTRACT_ADDRESS as `0x${string}`,
        abi: BaseVibeAbi,
        functionName: 'getUserStreak',
        args: [addr],
      }))
  }, [voterAddresses])

  // 5. Execute multicall to get streaks
  const { data: streaks, isLoading } = useReadContracts({
    contracts: streakCalls,
    query: {
      enabled: streakCalls.length > 0,
      refetchInterval: 30000,
    },
  })

  // 6. Process data and build RAW list (Top 120 to allow for deduping)
  useEffect(() => {
    if (!streaks || !voterAddresses) return

    const entries: LeaderboardEntry[] = []
    const todayKey = Math.floor(Date.now() / 1000 / 86400) // Current date key in UTC

    streaks.forEach((streakResult, index) => {
      const address = voterAddresses[index]?.result as unknown as string

      if (streakResult.status === 'success' && address) {
        const [currentStreak, lastVote, longestStreak] = streakResult.result as unknown as [bigint, bigint, bigint]

        const lastVoteNum = Number(lastVote)
        const isStreakActive = (todayKey - lastVoteNum) <= 1
        const displayStreak = isStreakActive ? Number(currentStreak) : 0

        if (displayStreak > 0) {
          entries.push({
            address,
            streak: displayStreak,
            longestStreak: Number(longestStreak),
            lastVote: lastVoteNum,
            rank: 0,
          })
        }

        // Keep track of user locally for "Your Rank" card
        if (userAddress && address.toLowerCase() === userAddress.toLowerCase()) {
          setUserEntry({
            address,
            streak: displayStreak,
            longestStreak: Number(longestStreak),
            lastVote: lastVoteNum,
            rank: 0
          })
        }
      }
    })

    // Sort by streak (highest first)
    entries.sort((a, b) => {
      if (b.streak !== a.streak) return b.streak - a.streak
      return b.longestStreak - a.longestStreak
    })

    // Take top 120 to have buffer for deduplication
    setRawEntries(entries.slice(0, 120))
  }, [streaks, voterAddresses, userAddress])

  // 7. Load cache on mount
  useEffect(() => {
    const cached = localStorage.getItem('basevibe_profiles')
    if (cached) {
      try {
        setProfileMap(JSON.parse(cached))
      } catch (e) {
        // invalid cache
      }
    }
  }, [])

  // 8. Fetch profiles for ALL raw entries (to enable deduplication)
  useEffect(() => {
    if (!NEYNAR_API_KEY || rawEntries.length === 0) return

    const fetchAllProfiles = async () => {
      // Find addresses missing from profileMap
      const missingAddresses = rawEntries
        .map(e => e.address)
        .filter(addr => !profileMap[addr.toLowerCase()])

      if (missingAddresses.length === 0) return

      // Batch fetch in chunks of 50
      const chunkSize = 50
      for (let i = 0; i < missingAddresses.length; i += chunkSize) {
        const chunk = missingAddresses.slice(i, i + chunkSize)

        try {
          const response = await fetch(
            `https://api.neynar.com/v2/farcaster/user/bulk-by-address?addresses=${chunk.join(',')}`,
            {
              headers: {
                'accept': 'application/json',
                'api_key': NEYNAR_API_KEY
              }
            }
          )
          const data = await response.json()

          if (data && Object.keys(data).length > 0) {
            const newProfiles: Record<string, { username: string, pfp_url: string }> = {}
            Object.keys(data).forEach(addr => {
              if (data[addr] && data[addr][0]) {
                newProfiles[addr.toLowerCase()] = {
                  username: data[addr][0].username,
                  pfp_url: data[addr][0].pfp_url
                }
              }
            })

            setProfileMap(prev => {
              const updated = { ...prev, ...newProfiles }
              localStorage.setItem('basevibe_profiles', JSON.stringify(updated))
              return updated
            })
          }
        } catch (e) {
          console.error("Failed to fetch profiles chunk", e)
        }
        await new Promise(r => setTimeout(r, 200))
      }
    }

    fetchAllProfiles()
  }, [rawEntries])

  // 9. Deduplicate and Finalize Leaderboard
  useEffect(() => {
    if (rawEntries.length === 0) return

    // Group by Identity (Username OR Address)
    const bestEntries = new Map<string, LeaderboardEntry>()

    rawEntries.forEach(entry => {
      const profile = profileMap[entry.address.toLowerCase()]
      const identity = profile ? profile.username : entry.address.toLowerCase()

      const existing = bestEntries.get(identity)
      if (!existing) {
        bestEntries.set(identity, entry)
      } else {
        // Keep higher streak
        if (entry.streak > existing.streak) {
          bestEntries.set(identity, entry)
        } else if (entry.streak === existing.streak) {
          if (entry.longestStreak > existing.longestStreak) {
            bestEntries.set(identity, entry)
          }
        }
      }
    })

    const finalEntries = Array.from(bestEntries.values()).sort((a, b) => {
      if (b.streak !== a.streak) return b.streak - a.streak
      return b.longestStreak - a.longestStreak
    })

    finalEntries.forEach((entry, index) => {
      entry.rank = index + 1
    })

    const top100 = finalEntries.slice(0, MAX_LEADERBOARD_SIZE)
    setLeaderboard(top100)

    // Update user entry rank if found
    if (userAddress) {
      const foundEntry = top100.find(e => e.address.toLowerCase() === userAddress.toLowerCase())
      if (foundEntry) {
        setUserEntry(foundEntry)
      }
    }

  }, [rawEntries, profileMap, userAddress])

  // Pagination logic needs to be after leaderboard is set
  const totalPages = Math.ceil(leaderboard.length / ITEMS_PER_PAGE)
  const paginatedLeaderboard = leaderboard.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  )

  const truncateAddress = (address: string) => {
    if (!address || address.length < 10) return address
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const handlePrevPage = () => setPage(p => Math.max(1, p - 1))
  const handleNextPage = () => setPage(p => Math.min(totalPages, p + 1))

  return (
    <div className="pb-24 pt-2 text-white">
      <div className="container mx-auto px-3 max-w-2xl">
        <h2 className="text-xl font-bold mb-4 text-center neon-text-purple">
          Global Leaderboard
        </h2>

        {/* Personal Rank Card */}
        {userAddress ? (
          <div className="mb-6 mx-1">
            <div className="glass-card p-4 rounded-2xl border border-purple-500/30 bg-purple-500/5 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold text-lg shadow-lg ring-2 ring-white/10">
                    {userEntry?.rank && userEntry.rank > 0 ? `#${userEntry.rank}` : '-'}
                  </div>
                  <div>
                    <p className="text-xs text-purple-200 font-bold uppercase tracking-wider">Your Rank</p>
                    <p className="text-sm font-mono text-white/90">{truncateAddress(userAddress)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-white flex items-center justify-end gap-1">
                    {userEntry?.streak || 0} <span className="text-lg">🔥</span>
                  </div>
                  <p className="text-[10px] text-purple-300/70 uppercase tracking-widest">Active Streak</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 mx-1 p-3 rounded-2xl bg-white/5 border border-white/10 text-center">
            <p className="text-sm text-gray-400">Connect wallet to see your rank</p>
          </div>
        )}

        {/* Global List */}
        <div className="glass-card rounded-3xl overflow-hidden border border-white/10 shadow-xl bg-black/40">
          {isLoading && leaderboard.length === 0 ? (
            <div className="p-12 text-center animate-pulse">
              <div className="text-4xl mb-3">⏳</div>
              <p className="text-gray-400 text-sm">Loading on-chain rankings...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-4xl mb-3">👻</div>
              <p className="text-gray-400 text-sm">
                No active streaks found.<br />Be the first to keep a streak alive!
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {paginatedLeaderboard.map((entry) => {
                const profile = profileMap[entry.address.toLowerCase()]

                return (
                  <div
                    key={entry.address}
                    className={`flex items-center justify-between p-4 hover:bg-white/5 transition-colors group ${userAddress && entry.address.toLowerCase() === userAddress.toLowerCase() ? 'bg-purple-500/10' : ''
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Rank Badge */}
                      <div
                        className={`relative flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm shadow-lg transition-transform group-hover:scale-110 ${entry.rank === 1
                          ? 'bg-gradient-to-br from-yellow-300 to-yellow-600 text-yellow-900 ring-2 ring-yellow-500/50'
                          : entry.rank === 2
                            ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-slate-900 ring-2 ring-slate-400/50'
                            : entry.rank === 3
                              ? 'bg-gradient-to-br from-orange-300 to-orange-600 text-orange-900 ring-2 ring-orange-500/50'
                              : 'bg-white/10 text-gray-400'
                          }`}
                      >
                        {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                      </div>

                      {/* Address/User & Stats */}
                      <div className="flex flex-col">
                        {profile ? (
                          <div className="flex items-center gap-2">
                            {profile.pfp_url && (
                              <img src={profile.pfp_url} alt="" className="w-4 h-4 rounded-full" />
                            )}
                            <p className="font-bold text-sm text-white hover:text-purple-300 transition-colors">
                              @{profile.username}
                            </p>
                          </div>
                        ) : (
                          <p className={`font-mono text-sm font-medium ${userAddress && entry.address.toLowerCase() === userAddress.toLowerCase()
                            ? 'text-purple-300'
                            : 'text-white'
                            }`}>
                            {truncateAddress(entry.address)}
                          </p>
                        )}

                        {entry.longestStreak > 0 && (
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                            Record: {entry.longestStreak} days
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Streak */}
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-sm ${entry.streak > 0
                      ? 'bg-orange-500/10 border-orange-500/20'
                      : 'bg-gray-500/10 border-gray-500/10 grayscale opacity-60'
                      }`}>
                      <span className={`text-lg ${entry.streak > 0 ? 'animate-pulse' : ''}`}>🔥</span>
                      <span className={`font-bold text-base ${entry.streak > 0 ? 'text-orange-400' : 'text-gray-400'}`}>
                        {entry.streak}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {leaderboard.length > ITEMS_PER_PAGE && (
            <div className="p-4 flex items-center justify-between border-t border-white/5 bg-white/5">
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-white disabled:text-gray-600 hover:text-purple-400 transition-colors"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-400 font-mono">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={page === totalPages}
                className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-white disabled:text-gray-600 hover:text-purple-400 transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Sync Status */}
        <div className="mt-4 flex justify-between items-center opacity-50 px-2">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
              Synced with Base
            </p>
          </div>
          {NEYNAR_API_KEY && (
            <p className="text-[10px] text-purple-400 font-medium uppercase tracking-widest">
              Farcaster Profiles Active
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
